import React, { useState, useEffect, useCallback, useRef } from 'react';
import Collapse from '../Common/Collapse';
import Loader from '../Common/Loader';
import StudentProfileModal from './StudentProfileModal';
import Modal from '../Common/Modal';
import NxSwitch from '../Common/NxSwitch';
import { CoverPattern, shade } from './CourseCover';
import { CourseIcon } from './iconLibrary';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import { getCourseOverview } from '../../services/adminService';

/* Экран «Статистика» (дизайн 3a): сверху вниз — карточки курсов, показатели
   выбранного курса, ниже двумя колонками модули с уроками и ученики.
   Оформление — компоненты, которые уже есть в проекте: обложка курса как на
   «Главной», модули как в «Вебинарах» (.mod), ученики как в разделе «Ученики». */

const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };


/* Что смотрим внутри модулей: тумблер тот же, что в «Уроках курса» на «Главной».
   ДЗ и варианты считаем по завершению, вебинары — по просмотру. */
const MODES = [
  { key: 'web', label: 'Вебинары' },
  { key: 'hw', label: 'ДЗ' },
  { key: 'var', label: 'Варианты' },
];
// Подпись к прогрессу модуля: вебинары смотрят, задания проходят
const MODE_DONE = { hw: 'Прошло', web: 'Просмотрели', var: 'Прошло' };
const MODE_WORDS = {
  hw: ['задание', 'задания', 'заданий'],
  web: ['вебинар', 'вебинара', 'вебинаров'],
  var: ['вариант', 'варианта', 'вариантов'],
};

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const fullDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return 'ничего не делал';
  const t = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${t}`;
};

// Дата под названием урока: «12 сентября», без года
const shortDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return null;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const relative = (iso) => {
  if (!iso) return 'давно';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return 'сегодня';
  if (d === 1) return 'вчера';
  if (d < 7) return `${d} ${plural(d, ['день', 'дня', 'дней'])} назад`;
  const w = Math.floor(d / 7);
  return `${w} ${plural(w, ['неделю', 'недели', 'недель'])} назад`;
};

/* Цвет курса на обложке: чуть отдаёт слева и уходит в тон карточки. Насыщенность
   разная по темам (в светлой нужен заметнее), поэтому оба варианта — переменными,
   а какой взять, решает CSS. Плашки уровня и иконки тоже красятся цветом курса. */
const hexRgba = (hex, a) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  const n = m ? parseInt(m[1], 16) : 0x7c4dff;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};
const coverVars = (color) => {
  const c = color || '#7c4dff';
  return {
    '--st-t-light': hexRgba(c, 0.5),
    '--st-t-dark': hexRgba(c, 0.22),
    '--st-chip': hexRgba(c, 0.22),
    '--st-chip-fg': shade(c, -42),
    '--st-chip-fg-dark': shade(c, 55),
    '--st-c': c,          // чистый цвет курса — для точки на плашке и узора в тёмной теме
  };
};

const IcUsers = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
const IcChart = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>);
const IcAlert = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>);

// Шеврон вправо: .mod.open поворачивает его на 90° — как у модулей в «Вебинарах»
const IcChevron = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>);
const IcCheck = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
const IcWeb = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8z" /><rect x="2" y="6" width="14" height="12" rx="2" /></svg>);
const IcHw = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const IcVar = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="2" /></svg>);
const IcModule = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5M2 12l10 5 10-5" /></svg>);

/* Карточка курса: обложка с узором сверху, под ней только «Прогресс»
   и число учеников — остальные цифры вынесены в показатели ниже. */
const CourseCard = ({ course, data, active, onPick }) => {
  const pct = data?.kpi?.avgProgress ?? 0;
  const students = data?.kpi?.students ?? 0;
  return (
    <button type="button" className={`st-crs${active ? ' on' : ''}`} onClick={onPick}>
      <div className="st-crs-cover" style={coverVars(course.color)}>
        <span className="st-crs-pat"><CoverPattern id={`st${course.id}`} /></span>
        <div className="st-crs-top">
          {course.examType && <span className="st-crs-lvl"><i />{course.examType}</span>}
          <span className="st-crs-ic"><CourseIcon icon={course.icon} size={18} /></span>
        </div>
        <div className="st-crs-name">{course.name}</div>
      </div>
      <div className="st-crs-body">
        <div className="st-crs-cell">
          <div className="st-crs-numrow">
            <span className="st-crs-pic"><IcChart /></span>
            <div className="st-crs-num">{pct}%</div>
          </div>
          <div className="st-crs-cap">прогресс</div>
        </div>
        {/* Справа — то же, что на карточке «учеников на курсе», зеркально */}
        <div className="st-crs-cell right">
          <div className="st-crs-numrow">
            <div className="st-crs-num">{students}</div>
            <span className="st-crs-pic"><IcUsers /></span>
          </div>
          <div className="st-crs-cap">учеников</div>
        </div>
      </div>
    </button>
  );
};

const CourseStatsView = ({ courses: allCourses }) => {
  // Черновики курса на статистике не показываем — только опубликованные
  const courses = React.useMemo(() => allCourses.filter(c => c.isActive !== false), [allCourses]);
  const [courseId, setCourseId] = useState(null);
  const [byCourse, setByCourse] = useState({});   // { [id]: overview }
  const [loading, setLoading] = useState(true);
  const [openModule, setOpenModule] = useState(null);
  const [mode, setMode] = useState('hw');       // ДЗ / вебинары / варианты
  const [studentId, setStudentId] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [lessonOpen, setLessonOpen] = useState(null);
  const [picker, setPicker] = useState(null);   // { risk } — окно выбора ученика
  // Высоту берём у карточки «Ученики»: она не листается, значит она и задаёт
  // размер строки — «Модули и уроки» подстраивается и листает список внутри.
  const [colH, setColH] = useState(null);
  const rosterRO = useRef(null);
  const rosterCard = useCallback((el) => {
    rosterRO.current?.disconnect();
    if (!el) return;
    const upd = () => setColH(Math.round(el.getBoundingClientRect().height));
    upd();
    rosterRO.current = new ResizeObserver(upd);
    rosterRO.current.observe(el);
  }, []);

  // Обзоры грузим сразу по всем курсам: цифры нужны на каждой карточке
  useEffect(() => {
    let alive = true;
    if (courses.length === 0) { setLoading(false); return undefined; }
    setLoading(true);
    Promise.all(courses.map(c => getCourseOverview(c.id).then(r => [c.id, r.data]).catch(() => [c.id, null])))
      .then(pairs => {
        if (!alive) return;
        const map = {}; pairs.forEach(([id, d]) => { map[id] = d; });
        setByCourse(map);
        setCourseId(prev => prev ?? courses[0].id);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [courses]);

  const data = courseId != null ? byCourse[courseId] : null;
  const students = data?.students || [];
  const student = students.find(s => s.id === studentId) || students[0] || null;

  const pickCourse = useCallback((id) => { setCourseId(id); setOpenModule(null); setStudentId(null); }, []);

  if (loading) return <Loader />;
  if (courses.length === 0) return <div className="pg-empty">Курсов пока нет</div>;

  const atRisk = students.filter(s => s.tone === 'risk');
  // Значок слева от названия урока — по выбранному режиму
  const ModeIcon = mode === 'web' ? IcWeb : mode === 'var' ? IcVar : IcHw;

  return (
    <>
      <div className="st-courses">
        {courses.map(c => (
          <CourseCard key={c.id} course={c} data={byCourse[c.id]}
            active={c.id === courseId} onPick={() => pickCourse(c.id)} />
        ))}
      </div>

      <div className="st-cols">
        {/* ── Ученики ── */}
        <section className="st-card" ref={rosterCard}>
          <div className="panel-cap nomb">Ученики</div>

          <button type="button" className="st-pick" onClick={() => setPicker({ risk: false })}>
            <IcUsers />Выбрать ученика
          </button>

          {student && (
            <div className="st-detail">
              <div className="st-detail-head">
                <span className="st-detail-ava" style={{ background: avatarColor(student.id) }}>
                  {student.avatar ? <img src={student.avatar} alt="" /> : <AvatarIcon id={student.id} size={26} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="st-detail-n">{student.name}</div>
                  <div className="st-detail-mail">{student.email}</div>
                </div>
                <span className="st-detail-pct">{student.pct}%</span>
              </div>

              <div className="st-cells">
                {[
                  { l: 'Вебинары', p: student.web, c: 'var(--text-1)', ic: <IcWeb /> },
                  { l: 'Домашние задания', p: student.hw, c: '#22c55e', ic: <IcHw /> },
                  { l: 'Варианты', p: student.variants, c: '#22c55e', ic: <IcVar /> },
                ].map(c => {
                  const [d, t] = c.p || [0, 0];
                  const pct = t ? Math.round((d / t) * 100) : 0;
                  return (
                    <div key={c.l} className="st-cell">
                      {/* Значок вынесен влево от обеих строк — текст и шкала
                          начинаются с одного уровня */}
                      <span className="st-cell-ic">{c.ic}</span>
                      <div className="st-cell-main">
                        <div className="st-cell-top">
                          <span className="st-cell-l">{c.l}</span>
                          <span className="st-cell-n">{d} / {t}</span>
                          <span className="st-cell-p">{pct}%</span>
                        </div>
                        <div className="st-cell-bar"><span style={{ width: `${pct}%`, background: c.c }} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="st-detail-row">
                <span>Последняя активность</span>
                <b className={student.tone === 'risk' ? 'risk' : undefined}>{fullDate(student.lastActivityAt)}</b>
              </div>

              <div className="st-detail-foot">
                {student.currentModule && <>Сейчас на <b>{student.currentModule}</b><i /></>}
                был(а) {relative(student.lastActiveAt)}
              </div>

              <button type="button" className="st-open" onClick={() => setProfileId(student.id)}>
                Открыть карточку ученика
              </button>
            </div>
          )}

          {data && (
            <button type="button" className="st-risk" onClick={() => setPicker({ risk: true })}
              title="Показать учеников без активности">
              <span className="st-kpi-ic bad"><IcAlert /></span>
              <span className="st-risk-v">{data.kpi.risk}</span>
              <span className="st-risk-txt">
                <span className="st-risk-l">в зоне риска</span>
                <span className="st-risk-s">нет активности 7+ дней</span>
              </span>
              <span className="st-risk-go"><IcChevron /></span>
            </button>
          )}
        </section>
        {/* ── Модули и уроки ── */}
        <section className="st-card st-modules" style={colH ? { height: colH } : undefined}>
          <div className="nx-head">
            <div className="panel-cap nomb">Модули и уроки</div>
            <NxSwitch value={mode} onChange={setMode} options={MODES} />
          </div>
          <div className="st-list">
            {mode === 'var' ? (
              /* Варианты лежат вне модулей — показываем их одним списком */
              (data?.variants || []).length === 0
                ? <div className="st-empty">В курсе пока нет вариантов</div>
                : (
                  <div className="tline st-tline">
                    {data.variants.map(v => (
                      <div key={v.id} className={`titem${v.done > 0 ? ' done' : ''}`}>
                        <button type="button" className="ccard st-hw"
                          onClick={() => setLessonOpen({ ...v, kind: 'var' })}
                          title="Показать, кто прошёл вариант">
                          <span className="st-hw-ic"><IcVar /></span>
                          <span className="st-hw-main">
                            <span className="st-hw-t">{v.title}</span>
                            <span className="st-hw-d">{shortDate(v.date) || 'без даты'}</span>
                          </span>
                          <span className="st-lesson-n">{v.done}<i>/{v.total}</i></span>
                        </button>
                      </div>
                    ))}
                  </div>
                )
            ) : (data?.modules || []).length === 0 ? (
              <div className="st-empty">В курсе пока нет модулей</div>
            ) : data.modules.map(m => {
              const open = openModule === m.id;
              const items = m.lessons.filter(l => (mode === 'hw' ? l.isHomeworkOnly : !l.isHomeworkOnly));
              // Считаем по тому, что показано в этом режиме: сколько учеников закрыли
              // все задания модуля (или просмотрели все его вебинары)
              const sets = items.map(l => new Set(mode === 'web' ? (l.openIds || []) : (l.doneIds || [])));
              const passed = items.length ? students.filter(s => sets.every(set => set.has(s.id))).length : 0;
              const pct = m.total ? Math.round((passed / m.total) * 100) : 0;
              return (
                <div key={m.id} className={`mod st-mod${open ? ' open' : ''}`}>
                  <div className="mod-head" onClick={() => setOpenModule(open ? null : m.id)}>
                    <div className="mod-ic"><IcModule /></div>
                    <div className="mod-info">
                      <div className="mod-title">{m.name}</div>
                      <div className="mod-sub">{items.length} {plural(items.length, MODE_WORDS[mode])}</div>
                    </div>
                    <div className="mod-prog">
                      <div className="mod-prog-top"><span>{MODE_DONE[mode]} {passed} / {m.total}</span><b>{pct}%</b></div>
                      <div className="mod-prog-bar"><span style={{ width: `${pct}%` }} /></div>
                    </div>
                    <span className="mod-count">{items.length}</span>
                    <span className="mod-chev"><IcChevron /></span>
                  </div>
                  <Collapse open={open} keepMounted>
                    <div className="mod-body">
                      {items.length === 0
                        ? <div className="st-empty">{mode === 'hw' ? 'В модуле нет домашних заданий' : 'В модуле нет вебинаров'}</div>
                        : (
                          <div className="tline st-tline">
                            {items.map(l => {
                              // Вебинар — сколько учеников его открыли, ДЗ — сколько сдали
                              const n = mode === 'web' ? (l.opened ?? 0) : l.done;
                              return (
                                <div key={l.id} className={`titem${n > 0 ? ' done' : ''}`}>
                                  <button type="button" className="ccard st-hw"
                                    onClick={() => setLessonOpen({ ...l, kind: mode })}
                                    title={mode === 'web' ? 'Показать, кто открыл вебинар' : 'Показать, кто сдал это задание'}>
                                    <span className="st-hw-ic"><ModeIcon /></span>
                                    <span className="st-hw-main">
                                      <span className="st-hw-t">{l.title}</span>
                                      <span className="st-hw-d">{shortDate(l.date) || 'без даты'}</span>
                                    </span>
                                    <span className="st-lesson-n">{n}<i>/{l.total}</i></span>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        </section>

      </div>

      {/* Выбор ученика: те же поля, что в разделе «Ученики» (без кода) */}
      <Modal isOpen={!!picker} onClose={() => setPicker(null)} bare>
        {picker && (() => {
          const list = picker.risk ? atRisk : students;
          const courseName = (courses.find(c => c.id === courseId) || {}).name || '';
          return (
            <div className="adm-modal st-pm" onClick={e => e.stopPropagation()}>
              <div className="st-lm-head">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="st-lm-t">{picker.risk ? 'В зоне риска' : 'Выбрать ученика'}</div>
                  <div className="st-lm-s">
                    {picker.risk ? 'нет активности 7+ дней · ' : ''}{list.length} {plural(list.length, ['ученик', 'ученика', 'учеников'])}
                  </div>
                </div>
                <button type="button" className="adm-x" onClick={() => setPicker(null)} aria-label="Закрыть">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              <div className="st-pm-body">
                <div className="st-pm-head">
                  <span>Ученик</span><span>Курс</span><span>Пройдено</span><span>Был(а) в сети</span><span />
                </div>
                {list.length === 0 ? (
                  <div className="st-empty">{picker.risk ? 'Все ученики активны' : 'На курсе нет учеников'}</div>
                ) : list.map(s2 => (
                  <button key={s2.id} type="button"
                    className={`st-pm-row${student && s2.id === student.id ? ' on' : ''}`}
                    onClick={() => { setStudentId(s2.id); setPicker(null); }}>
                    <span className="st-pm-user">
                      <span className="st-pm-ava" style={{ background: avatarColor(s2.id) }}>
                        {s2.avatar ? <img src={s2.avatar} alt="" /> : <AvatarIcon id={s2.id} size={24} />}
                      </span>
                      <span className="st-pm-id">
                        <span className="st-pm-n">{s2.name}</span>
                        <span className="st-pm-mail">{s2.email}</span>
                      </span>
                    </span>
                    <span className="st-pm-course">{courseName}</span>
                    <span className="st-pm-done">
                      <span className="st-pm-bar"><span style={{ width: `${s2.pct}%` }} /></span>
                      <b>{s2.pct}%</b>
                    </span>
                    <span className="st-pm-seen">{relative(s2.lastActiveAt)}</span>
                    <span className="st-pm-go"><IcChevron /></span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Кто закрыл урок: все ученики курса по алфавиту, по трое в ряд */}
      <Modal isOpen={!!lessonOpen} onClose={() => setLessonOpen(null)} bare>
        {lessonOpen && (() => {
          // Вебинар засчитан по просмотру, ДЗ и вариант — по завершению
          const done = new Set(lessonOpen.kind === 'web' ? (lessonOpen.openIds || []) : (lessonOpen.doneIds || []));
          const verb = lessonOpen.kind === 'web' ? 'открыли' : lessonOpen.kind === 'var' ? 'прошли' : 'сдали';
          const sorted = [...students].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
          return (
            <div className="adm-modal st-lm" onClick={e => e.stopPropagation()}>
              <div className="st-lm-head">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="st-lm-t">{lessonOpen.title}</div>
                  <div className="st-lm-s">
                    {verb} {done.size} из {sorted.length}
                  </div>
                </div>
                <button type="button" className="adm-x" onClick={() => setLessonOpen(null)} aria-label="Закрыть">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              <div className="st-lm-list">
                {sorted.length === 0
                  ? <div className="st-empty">На курсе нет учеников</div>
                  : sorted.map(st2 => {
                    const ok = done.has(st2.id);
                    return (
                      <div key={st2.id} className={`st-lm-row${ok ? ' ok' : ''}`}>
                        <span className={`st-lm-box${ok ? ' ok' : ''}`}>{ok && <IcCheck />}</span>
                        <span className="st-lm-ava" style={{ background: avatarColor(st2.id) }}>
                          {st2.avatar ? <img src={st2.avatar} alt="" /> : <AvatarIcon id={st2.id} size={20} />}
                        </span>
                        <span className="st-lm-main">
                          <span className="st-lm-n">{st2.name}</span>
                          <span className="st-lm-code">{st2.code || '····'}</span>
                        </span>
                        {/* Результат по номерам — только у сдавших и только там, где номера есть */}
                        {ok && lessonOpen.tasks > 0 && (
                          <span className="st-lesson-n st-lm-res">
                            {(lessonOpen.correct || {})[st2.id] || 0}<i>/{lessonOpen.tasks}</i>
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Карточка ученика — тот же компонент, что в разделе «Ученики».
          Здесь она только для просмотра: управление аккаунтом живёт там. */}
      <StudentProfileModal
        isOpen={profileId != null}
        student={profileId != null ? (() => {
          const s2 = students.find(x => x.id === profileId);
          return s2 ? { id: s2.id, name: s2.name, email: s2.email, progressCode: s2.code, avatar: s2.avatar } : null;
        })() : null}
        onClose={() => setProfileId(null)}
        readOnly
      />
    </>
  );
};

export default CourseStatsView;
